'use client';

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchContentModerationSummary,
  provenanceKeys,
  type ContentModerationSummary,
  type DetectionType,
} from '@/shared/cors-provenance-api';
import { isAllCasesSelection } from '@/shared/case-summary';
import { useProvenanceSession } from './useProvenanceSession';
import { useContentModerationCase } from './useContentModerationFolderName';

/**
 * TanStack Query hook that fetches content moderation summary counts for a
 * given case from ``GET /provenance-service/v1/content-moderation/summary``.
 *
 * Auth wiring (``cellId`` + ``realAccessToken`` + ``enabled``) follows the
 * same pattern as every other provenance-service hook (see ``useCasesList``).
 *
 * ``caseId`` is the nav selection (a display name); the wire ``case_id`` is the
 * case's FOLDER SEGMENT. See {@link useContentModerationFolderName} — findings
 * are stored under the segment, so the display name reads an empty case.
 * All Cases (``*``) is not a case: the query stays disabled until the user
 * picks one.
 *
 * Returned fields (from the backend ``ContentModerationSummaryResponse``):
 *   - ``flaggedCount``   — items awaiting manual review (status FLAGGED)
 *   - ``reviewedCount``  — items reviewed but not yet confirmed (status REVIEWED)
 *   - ``confirmedCount`` — items confirmed as CSAM (status CONFIRMED)
 */
export function useContentModerationSummary(
  caseId: string | null | undefined,
  detectionType: DetectionType = 'CSAM',
): UseQueryResult<ContentModerationSummary, Error> & { isCaseResolving: boolean } {
  const { cellId, accessToken, enabled } = useProvenanceSession();
  const { folderName, isResolving: isCaseResolving } = useContentModerationCase(caseId);

  const query = useQuery<ContentModerationSummary, Error>({
    queryKey: provenanceKeys.contentModerationSummary(cellId, folderName, detectionType),
    queryFn: ({ signal }) =>
      fetchContentModerationSummary({
        cellId: cellId as string,
        accessToken: accessToken as string,
        caseId: folderName as string,
        detectionType,
        signal,
      }),
    enabled: enabled && Boolean(folderName) && !isAllCasesSelection(caseId),
    staleTime: 30 * 1000,
  });

  // A disabled query looks settled-and-empty. Hand the caller the one extra bit
  // it needs to tell "still resolving the case" from "this case has no findings".
  return Object.assign(query, { isCaseResolving });
}
